import { FunctionComponent, useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
	AutoCompleteItem as DropdownAutoCompleteItem,
	Dropdown,
} from "@/components/Dropdown/Dropdown";

import { INHERIT_OPTION_ID } from "../utils/listEmailDeliverability.utils";

interface EmailOverrideRowProps {
	testId: string;
	title: string;
	description: string;
	/** The explicit choices; the "Workspace default" option is put in front of them here. */
	choices: DropdownAutoCompleteItem[];
	/** The list's own choice, or `null` when it uses the workspace default. */
	selectedId: string | null;
	/** The workspace default's choice, or `undefined` while Company Settings could not be read. */
	workspaceId: string | undefined;
	onChange: (choiceId: string | null) => void;
}

/**
 * One overridable setting in the list's Email deliverability section (PREC-4261): a dropdown whose
 * first option is "Workspace default (currently: X)", a badge when the list's choice differs from
 * that default, and a "Use workspace default" link that stores NULL again. When the workspace value
 * is unknown the first option reads "Workspace default" and no badge claims a difference.
 */
export const EmailOverrideRow: FunctionComponent<EmailOverrideRowProps> = ({
	testId,
	title,
	description,
	choices,
	selectedId,
	workspaceId,
	onChange,
}) => {
	const { t } = useTranslation();
	const workspaceTitle = choices.find((choice) => choice.id === workspaceId)?.title;

	const options = useMemo<DropdownAutoCompleteItem[]>(
		() => [
			{
				id: INHERIT_OPTION_ID,
				title: workspaceTitle
					? t("contacts.settings.listDeliverability.workspaceDefaultOption", {
							value: workspaceTitle,
						})
					: t("contacts.settings.listDeliverability.workspaceDefaultOptionUnknown"),
			},
			...choices,
		],
		[choices, t, workspaceTitle]
	);
	const selected = options.find((option) => option.id === (selectedId ?? INHERIT_OPTION_ID));
	const overridesDefault = selectedId !== null && !!workspaceTitle && selectedId !== workspaceId;

	return (
		<div className="flex flex-col gap-2" data-testid={testId}>
			<div className="flex items-center gap-2">
				<span className="text-ssm font-medium text-text">{title}</span>
				{overridesDefault && (
					<span
						className="rounded bg-status-warning-light px-1.5 py-0.5 text-xs text-text"
						data-testid={`${testId}-override-badge`}
					>
						{t("contacts.settings.listDeliverability.overridesWorkspaceDefault", {
							value: workspaceTitle,
						})}
					</span>
				)}
			</div>
			<p className="text-xs text-text-meta">{description}</p>
			<div className="flex items-center gap-4">
				<div className="w-full max-w-[460px]">
					{/* Keyed by the choice: the Dropdown shows its last pick over a new `defaultValue`, so
					    "Use workspace default" (or a switch of list) remounts it with the stored choice. */}
					<Dropdown
						key={selectedId ?? INHERIT_OPTION_ID}
						floating
						portal
						data={options}
						defaultValue={selected}
						testId={`${testId}-dropdown`}
						handleSelect={(item) =>
							onChange(item.id === INHERIT_OPTION_ID ? null : String(item.id))
						}
					/>
				</div>
				{selectedId !== null && (
					<button
						className="cursor-pointer text-xs font-medium text-blue-action hover:underline"
						data-testid={`${testId}-use-default`}
						type="button"
						onClick={() => onChange(null)}
					>
						{t("contacts.settings.listDeliverability.useWorkspaceDefault")}
					</button>
				)}
			</div>
		</div>
	);
};
